<script setup lang="ts">
definePageMeta({
  layout: "how-we-work-layout",
});

useHead({
  title:
    "WebReinvent is a company with a proven process that delivers high-quality software products.",
  meta: [
    {
      name: "description",
      content: "Ever wondered what goes in the making of stunning and inspiring websites that have the power to instill awe. Here is how it works. Here is how WebReinvent works."
    }
  ],
});

import { useGlobalStore } from "~/stores/globalStore";
const globalStore = useGlobalStore();
</script>
<template>
  <div class="max-w-screen-xl mx-auto px-4 mt-[80px] relative">
    <NuxtImg v-if="$colorMode.preference== 'dark'" src="/images/howWeWorkPageBg.webp" class="fixed inset-0 size-full" width="486" height="695" />
    <section class="z-10 relative">
      <div class="bg-contain bg-right">
        <h1
          class="xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl max-md:text-2xl mb-2 max-md:mb-4 md:mb-4 max-xl:text-center"
        >
          <WrGradientText
            text="Building with"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_0.57%,_var(--text-gradient-2)_12.72%)]"
          />
          Precision & Purpose
        </h1>
        <p class="text-2xl max-md:text-xl max-xl:text-center tracking-[0.5px]">
          A structured, data-driven journey to digital transformation.
        </p>
      </div>

      <!-- HOW WE WORK FLOW MAP SECTION -->
      <div
        class="max-xl:grid lg:grid-cols-2 xl:flex xl:flex-wrap xl:justify-between justify-center gap-[18px] mt-24 max-lg:max-w-lg max-lg:mx-auto"
      >
        <!-- card 1 -->
        <div
          class="p-px bg-[image:var(--border-how-we-work-card)] rounded-xl max-xl:w-full"
        >
          <div
            class="bg-[image:var(--bg-how-we-work-card)] rounded-xl overflow-hidden h-full w-full xl:max-w-[510px] gap-6 pt-6 flex flex-col justify-between items-center"
          >
            <div class="px-3 md:px-7">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  1.
                </h2>
                <h3 class="text-2xl  lg:text-[34px] lg:leading-[42px]">
                  <WrGradientText append-class="py-0">
                    Software Requirement <br />
                    Specification
                  </WrGradientText>
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">Requirement Specification </li>
                <li class="font-normal">Document (SRS)</li>
                <li class="font-normal">Q & A Session</li>
                <li class="font-normal">Competitor Analysis</li>
              </ul>
            </div>
            <NuxtImg
              alt="Software Requirement Specification"
              :src="
                $colorMode.preference== 'dark'
                  ? '/images/darkSoftwareReq.webp'
                  : '/images/softwareReq.webp'
              "
              height="324"
              width="423"
              loading="lazy"
              format="webp"
            />
          </div>
        </div>

        <!-- card 2 -->
        <div
          class="p-px bg-[image:var(--border-how-we-work-card)] rounded-xl overflow-hidden max-xl:w-full"
        >
          <div
            class="bg-[image:var(--bg-how-we-work-card)] dark:bg-[linear-gradient(146.45deg,_rgba(17,_7,_22,_0.7)_7.65%,_rgba(63,0,119,0.7)_75.32%,_rgba(103,39,147,0.7)_98.15%)] rounded-xl h-full w-full xl:max-w-[359px] xl:min-w-[359px] pt-6 flex flex-col gap-6 justify-between"
          >
            <div class="px-3 md:px-7">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  2.
                </h2>
                <h3 class="text-2xl lg:text-[34px] lg:leading-[42px] lg:whitespace-nowrap">
                  <WrGradientText text="UFD & Wireframe" append-class="py-0" />
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">User Journey & Operating Conditions</li>
                <li class="font-normal">User Experience </li>
                <li class="font-normal">Low & High Fidelity Wireframes</li>
              </ul>
            </div>
            <div class="relative max-h-[320px] overflow-hidden">
              <NuxtImg
                alt="UFD and WireFrame"
                :src="
                  $colorMode.preference== 'dark'
                    ? '/images/darkUfdAndWireFrame.webp'
                    : '/images/ufdAndWIreFrame.webp'
                "
                height="320"
                width="358"
                class="mx-auto"
                loading="lazy"
                format="webp"
              />
              <div
                class="absolute inset-0 w-full h-full bg-black opacity-20 [mask-image:url('/images/howWeWorkBgPattern.webp')] dark:[mask-image:url('/images/darkHowWeWorkBgPattern.webp')] [mask-repeat:no-repeat] [mask-size:cover] [mask-position:center] [--tw-mask-image:url('/images/howWeWorkBgPattern.webp')] [--tw-mask-repeat:no-repeat] [--tw-mask-size:cover] [--tw-mask-position:center]"
              ></div>
            </div>
          </div>
        </div>

        <!-- card 3 -->
        <div
          class="relative overflow-hidden p-px bg-[image:var(--border-how-we-work-card)] rounded-xl after:content-[''] after:absolute after:right-[40px] after:-top-40 after:w-[55px] after:h-[500px] after:rotate-[38deg] after:blur-[50px] after:bg-[linear-gradient(180.37deg,_rgba(181,177,183,0.5)_29.35%,_rgba(124,124,124,0.5)_131.96%)] dark:after:bg-[rgba(116,32,158,1)] max-xl:w-full"
        >
          <div
            class="bg-[image:var(--bg-how-we-work-card)] rounded-xl h-full w-full xl:max-w-[359px] xl:min-w-[359px] pl-3 md:pl-7 pt-6 flex flex-col gap-8 justify-between pr-0"
          >
            <div class="pr-3 relative z-10">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  3.
                </h2>
                <h3 class="text-2xl lg:text-[34px] lg:leading-[42px]">
                  <WrGradientText text="Design & HTML" append-class="py-0" />
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">Design Direction and User Interface</li>
                <li class="font-normal">Code Optimization</li>
                <li class="font-normal">Minification of Assets</li>
              </ul>
            </div>

            <div class="w-full flex items-end justify-end">
              <NuxtImg
                alt="Design and HTML"
                :src="
                  $colorMode.preference== 'dark'
                    ? '/images/darkDesignAndHTML.webp'
                    : '/images/designAndHtml.webp'
                "
                height="322"
                width="324"
                loading="lazy"
                format="webp"
              />
            </div>
          </div>
        </div>

        <div class="max-xl:hidden xl:ml-12 xl:mt-4">
          <NuxtImg
            alt="Data Driven GIF"
            :src="
              $colorMode.preference== 'dark'
                ? '/images/darkDataDriven.webp'
                : '/images/dataDriven.webp'
            "
            height="304"
            width="304"
            class="dark:bg-[image:url('/images/darkDataDrivenBg.webp')] bg-center"
            loading="lazy"
            format="webp"
          />
        </div>

        <!-- card 4 -->
        <div
          class="relative overflow-hidden p-px max-xl:w-full bg-[image:var(--border-how-we-work-card)] rounded-xl after:content-[''] after:absolute after:right-[215px] after:-top-40 after:w-[55px] after:h-[500px] after:rotate-[38deg] after:blur-[50px] after:bg-[ ] dark:after:bg-[linear-gradient(180.37deg,_rgba(160,102,199,0.5)_29.35%,_rgba(59,12,91,0.5)_131.96%)]"
        >
          <div
            class="bg-[image:var(--bg-how-we-work-card),url('/images/howWeWorkGridPattern.webp')] bg-cover rounded-xl xl:max-w-[736px] xl:min-w-[736px] h-full w-full pt-6 flex flex-col gap-8"
          >
            <div class="px-3 md:px-7">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  4.
                </h2>
                <h3 class="text-2xl  lg:text-[34px] lg:leading-[42px] pr-10">
                  <WrGradientText text="Development" append-class="py-0" />
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">
                  Database Structure (Visual ER Diagram)
                </li>
                <li class="font-normal">GitFlow, DevOps & Code Review</li>
                <li class="font-normal">Code Coverage</li>
              </ul>
            </div>

            <!-- <div class="w-full flex items-end justify-end"> -->
            <NuxtImg
              alt="Development"
              :src="
                $colorMode.preference== 'dark'
                  ? '/images/darkDevelopment.webp'
                  : '/images/development.webp'
              "
              height="160"
              width="778"
              class="mt-5"
              loading="lazy"
              format="webp"
            />
            <!-- </div> -->
          </div>
        </div>

        <!-- card 5 -->
        <div
          class="relative p-px max-xl:w-full overflow-hidden bg-[image:var(--border-how-we-work-card)] rounded-xl after:content-[''] after:absolute after:right-[110px] after:-top-40 after:w-[55px] after:h-[500px] after:rotate-[38deg] after:blur-[50px] after:bg-[linear-gradient(180.37deg,rgba(181,177,183,0.5)_29.35%,rgba(124,124,124,0.5)_131.96] dark:after:bg-[linear-gradient(180.37deg,_#A066C7_29.35%,_#3B0C5B_131.96%)]"
        >
          <div
            class="dark:bg-[image:url('/images/howWeWorkGridPattern.webp'),#110716B2] bg-[image:var(--bg-how-we-work-card),url('/images/howWeWorkGridPattern.webp')] rounded-xl w-full xl:max-w-[510px] xl:min-w-[488px] pt-6 flex flex-col gap-8 overflow-hidden"
          >
            <div class="px-3 md:px-7">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  5.
                </h2>
                <h3 class="text-2xl  lg:text-[34px] lg:leading-[42px] pr-10">
                  <WrGradientText
                    text="Automation Testing"
                    append-class="py-0"
                  />
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">Browser Automation Testing</li>
                <li class="font-normal">CI/CD Pipeline & API Testing</li>
                <li class="font-normal">Multiple Platform Testing</li>
              </ul>
            </div>

            <div class="w-full flex items-end justify-end overflow-hidden">
              <NuxtImg
                alt="Automation Testing"
                :src="
                  $colorMode.preference== 'dark'
                    ? '/images/darkRadarTesting.webp'
                    : '/images/radartesting.webp'
                "
                height="198"
                width="355"
                class="mt-5 overflow-hidden"
                loading="lazy"
                format="webp"
              />
            </div>
          </div>
        </div>

        <!-- card 6 -->
        <div
          class="p-px max-xl:w-full bg-[image:var(--border-how-we-work-card)] rounded-xl"
        >
          <div
            class="bg-[image:var(--bg-how-we-work-card),url('/images/howWeWorkBgPattern.webp')] dark:bg-[url('/images/darkHowWeWorkBgPattern.webp'),linear-gradient(#110716_0%,_#3F0077_100%)] rounded-xl overflow-hidden xl:max-w-[736px] xl:min-w-[736px] w-full h-full gap-8 bg-cover pt-6 flex flex-col relative"
          >
            <div class="px-3 md:px-7">
              <div class="flex justify-start gap-3 items-center">
                <h2
                  class="2xl:text-5xl  lg:text-[34px] lg:leading-[42px] text-3xl text-black dark:text-white"
                >
                  6.
                </h2>
                <h3 class="text-2xl  lg:text-[34px] lg:leading-[42px] pr-10">
                  <WrGradientText text="Documentation" append-class="py-0" />
                </h3>
              </div>
              <ul class="list-disc pl-12 mt-4 max-md:text-sm space-y-2">
                <li class="font-normal">Developers Guide and Code Standards</li>
                <li class="font-normal">Code Explanation & Snippet</li>
                <li class="font-normal">Important Technical Information</li>
              </ul>
            </div>

            <!-- <div class="w-full flex items-end justify-end"> -->
            <NuxtImg
              alt="Documentation"
              :src="
                $colorMode.preference== 'dark'
                  ? '/images/darkDocumentation.webp'
                  : '/images/documentation.webp'
              "
              height="221"
              width="778"
              class=""
              loading="lazy"
              format="webp"
            />
            <!-- </div> -->
          </div>
        </div>
      </div>
    </section>

    <section class="mt-[140px] relative">
      <span
        class="dark:hidden size-[377px] bg-[linear-gradient(180deg,_#FFFFFF_0%,_#D7D7D7_100%)] rounded-full absolute -top-[150px] -left-96 opacity-90"
      />
      <div
        class="hidden dark:block -translate-x-1/2 left-[48%] absolute w-[2266px] h-[841px] top-10 rounded-[100%] bg-[linear-gradient(0deg,_#2D1141_31.11%,_#5E2E87_71.33%,_#471565_77.98%,_#5E258E_81.05%)] blur-[150px] dark:blur-[100px] z-0 opacity-80"
      >
        <div
          class="w-[2390px] h-[650px] bg-[rgba(10,0,17,1)] rounded-[100%] absolute top-[200px] left-0 blur-[100px]"
        ></div>
      </div>
      <WrResultSection />
    </section>

    <section class="lg:mt-[128px] mt-16 relative z-10">
      <h2 class="text-center">
        <WrGradientText
          text="Trusted for excellence and efficiency"
          append-class="text-3xl lg:text-[48px]"
        />
        <p class="text-lg text-[var(--foreground)]">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h2>
      <WrTestimonialCardCarousel class="mt-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </section>
  </div>
</template>

<style scoped></style>
